import { useState, useCallback } from 'react';
import { Dialog, Typography, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { subDays, startOfMonth, addMonths, differenceInMonths } from 'date-fns';
import { I18N_KEYS } from '@/language/keys';
import { color } from '@/colors';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { AppXBtn } from '@/UI/AppXBtn/AppXBtn';
import { Dropdown, type Option } from '@/UI/Dropdown/Dropdown';
import { log } from '@/logger/logger';
import { useUserStore } from '@/state/user.state';
import { downloadCreditsReport } from '@/api/workspaceSettings';
import { DATE_FORMATS } from '@/consts/date.consts';
import { formatInGMT, startOfGMTDay } from '@/utils/date.utils';
import { downloadCSV } from './utils';

const logger = log.getLogger('CreditsReportModal');

enum DateRangeOption {
  CurrentCycle = 'current_cycle',
  Last30Days = 'last_30_days',
  Last90Days = 'last_90_days',
}

interface CreditsReportModalProps {
  open: boolean;
  onClose: () => void;
}

export const CreditsReportModal = ({ open, onClose }: CreditsReportModalProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeOption>(DateRangeOption.CurrentCycle);
  const activeWorkspace = useUserStore((state) => state.user?.activeWorkspace);
  const subscription = activeWorkspace?.subscription;

  const calculateStartDate = useCallback(
    (dateRangeOption: DateRangeOption): Date => {
      const today = new Date();

      switch (dateRangeOption) {
        case DateRangeOption.Last30Days:
          return startOfGMTDay(subDays(today, 30));

        case DateRangeOption.Last90Days:
          return startOfGMTDay(subDays(today, 90));

        case DateRangeOption.CurrentCycle: {
          if (!subscription?.startsAt) {
            // Fallback to current month if no subscription start date
            return startOfGMTDay(startOfMonth(today));
          }

          const subscriptionStartDate = new Date(subscription.startsAt);

          // Calculate how many complete months have passed since subscription started
          const monthsSinceStart = differenceInMonths(today, subscriptionStartDate);

          // Get the start of the current billing cycle
          const currentCycleStart = addMonths(subscriptionStartDate, monthsSinceStart);

          return currentCycleStart;
        }

        default:
          return startOfGMTDay(today);
      }
    },
    [subscription?.startsAt],
  );

  const dateRangeOptions: Option<DateRangeOption>[] = [
    {
      value: DateRangeOption.CurrentCycle,
      label: t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CREDITS_REPORT_MODAL.DATE_RANGE_CURRENT_CYCLE),
      id: DateRangeOption.CurrentCycle,
    },
    {
      value: DateRangeOption.Last30Days,
      label: t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CREDITS_REPORT_MODAL.DATE_RANGE_LAST_30_DAYS),
      id: DateRangeOption.Last30Days,
    },
    {
      value: DateRangeOption.Last90Days,
      label: t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CREDITS_REPORT_MODAL.DATE_RANGE_LAST_90_DAYS),
      id: DateRangeOption.Last90Days,
    },
  ];

  const handleConfirm = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const startDate = calculateStartDate(selectedDateRange);
      const creditsReport = await downloadCreditsReport(startDate);

      // Generate CSV filename
      const workspaceName = activeWorkspace?.workspaceName || 'workspace';
      const startDateStr = formatInGMT(startDate, DATE_FORMATS.API_DATE);
      const todayDateStr = formatInGMT(new Date(), DATE_FORMATS.API_DATE);
      const fileName = `${workspaceName}_usage_${startDateStr}_to_${todayDateStr}.csv`;
      downloadCSV(creditsReport, fileName);
      onClose();
    } catch (error) {
      logger.error('Failed to download credits report:', error);
      setErrorMessage(t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CREDITS_REPORT_MODAL.ERROR_MESSAGE));
    } finally {
      setIsLoading(false);
    }
  }, [selectedDateRange, onClose, calculateStartDate, t, activeWorkspace?.workspaceName]);

  const handleDateRangeChange = useCallback((option: Option<DateRangeOption>) => {
    setSelectedDateRange(option.value);
    setErrorMessage(null);
  }, []);

  const handleClose = useCallback(() => {
    setErrorMessage(null);
    if (!isLoading) {
      onClose();
    }
  }, [onClose, isLoading]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2,
        },
      }}
    >
      <FlexCol
        sx={{
          width: '420px',
          p: 3,
          pt: 5.25,
          backgroundColor: color.Black92,
        }}
      >
        <Typography variant="body-lg-sb">
          {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CREDITS_REPORT_MODAL.TITLE)}
        </Typography>
        <FlexCol sx={{ gap: 1, my: 4 }}>
          <Typography variant="body-sm-rg" color={color.White80_T}>
            {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CREDITS_REPORT_MODAL.TIME_PERIOD_LABEL)}
          </Typography>
          <Dropdown
            value={selectedDateRange}
            options={dateRangeOptions}
            onChange={handleDateRangeChange}
            width="100%"
            size="large"
          />
          {errorMessage && (
            <Typography variant="body-sm-rg" color={color.Weavy_Error} sx={{ mt: 1 }}>
              {errorMessage}
            </Typography>
          )}
        </FlexCol>
        <FlexCenVer sx={{ justifyContent: 'flex-end', gap: 1 }}>
          <ButtonContained size="medium" mode="text" onClick={handleClose} disabled={isLoading}>
            {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CREDITS_REPORT_MODAL.CANCEL_BUTTON)}
          </ButtonContained>
          <ButtonContained
            size="medium"
            onClick={() => void handleConfirm()}
            disabled={isLoading}
            endIcon={isLoading ? <CircularProgress size={12} sx={{ color: color.White64_T }} /> : null}
          >
            {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CREDITS_REPORT_MODAL.DOWNLOAD_BUTTON)}
          </ButtonContained>
        </FlexCenVer>
      </FlexCol>
      <AppXBtn sx={{ position: 'absolute', top: 12, right: 12 }} onClick={handleClose} disabled={isLoading} />
    </Dialog>
  );
};
